import React, { useState } from 'react';
import classNames from 'classnames';
import { ForgotPassword } from '../ForgotPassword';
import { DefaultValues } from './Authentication';
import { Register, RegisterFormValues } from './Register';
import { PasswordSignIn, SignInFormValues } from './PasswordSignIn';

type PasswordAuthenticationProps = {
  fullPage?: boolean;
  defaultValues?: DefaultValues;
  onAuth: () => void;
  showAuthModal?: () => void;
  hideAuthModal?: () => void;
};

const SIGN_IN_TAB = 'SIGN IN';
const REGISTER_TAB = 'REGISTER';
const FORGOT_PASSWORD_TAB = 'FORGOT PASSWORD';

const isSignInFormValues = (values?: DefaultValues): values is Partial<SignInFormValues> =>
  values != null && 'signInEmail' in values;

const isRegisterFormValues = (values?: DefaultValues): values is Partial<RegisterFormValues> =>
  values != null && 'registerEmail' in values;

export const PasswordAuthentication: React.FC<PasswordAuthenticationProps> = ({
  fullPage = false,
  defaultValues,
  onAuth,
  showAuthModal,
  hideAuthModal
}) => {
  const [activeTab, setActiveTab] = useState(
    isRegisterFormValues(defaultValues) ? REGISTER_TAB : SIGN_IN_TAB
  );
  const registerActive = activeTab === REGISTER_TAB;
  const signInActive = activeTab === SIGN_IN_TAB;
  const forgotPasswordActive = activeTab === FORGOT_PASSWORD_TAB;
  const handleClickForgotPassword = !fullPage ? () => setActiveTab(FORGOT_PASSWORD_TAB) : undefined;
  const showForgotPassword = !fullPage && forgotPasswordActive;
  const showSignInRegister = fullPage || !forgotPasswordActive;

  return (
    <>
      <div
        className={classNames('Authentication__header', {
          'Authentication__header--hidden': forgotPasswordActive
        })}
      >
        <button
          type="button"
          className={classNames('Authentication__tab', {
            'Authentication__tab--active': registerActive
          })}
          data-testid="registerTab"
          onClick={() => setActiveTab(REGISTER_TAB)}
        >
          {REGISTER_TAB}
        </button>
        <button
          type="button"
          className={classNames('Authentication__tab', {
            'Authentication__tab--active': signInActive
          })}
          data-testid="signInTab"
          onClick={() => setActiveTab(SIGN_IN_TAB)}
        >
          {SIGN_IN_TAB}
        </button>
      </div>

      <div className="Authentication__content">
        {showSignInRegister && (
          <>
            <Register
              className={classNames('Authentication__section', {
                'Authentication__section--active': registerActive
              })}
              defaultValues={isRegisterFormValues(defaultValues) ? defaultValues : undefined}
              onAuth={onAuth}
              showAuthModal={showAuthModal}
              hideAuthModal={hideAuthModal}
              showCancelButton={!fullPage}
            />
            <PasswordSignIn
              className={classNames('Authentication__section', {
                'Authentication__section--active': signInActive
              })}
              defaultValues={isSignInFormValues(defaultValues) ? defaultValues : undefined}
              onAuth={onAuth}
              onClickForgotPassword={handleClickForgotPassword}
              showAuthModal={showAuthModal}
              hideAuthModal={hideAuthModal}
              showCancelButton={!fullPage}
              title="Have an Account?"
              message="Sign in to speed up the checkout process, check your order status and save your favorite products."
            />
          </>
        )}

        {showForgotPassword && (
          <ForgotPassword
            className={classNames('Authentication__section', {
              'Authentication__section--active': forgotPasswordActive
            })}
            onClickSignIn={() => setActiveTab(SIGN_IN_TAB)}
          />
        )}
      </div>
    </>
  );
};
